/**
 * The DOCX export on the main thread (decisions 7, 14, 60, 64; part 22, table K″): the book read as it is now (part 21's
 * table N, the open chapter as typed) with its chapters' IDs and each chapter's export base pinned (table PN, one store
 * write), a DOCX worker started on it, its progress shown, and the finished file offered to save. The worker does the
 * work, so editing goes on while a book exports (decision 14). Cancelling ends the worker, which keeps nothing; the file
 * offered is let go once the writer has saved it or turned the offer down.
 *
 * The book's reading and pinning, the worker, the clock and the browser's object URLs are injected, so the logic runs
 * in tests.
 */
import type { ChapterIds, DocxPreset } from '@cockatrice/export/docx';
import type { PdfBook } from '@cockatrice/layout';
import { createSignal } from 'solid-js';
import type { Accessor } from 'solid-js';
import { fileName } from '../export/controller.ts';
import { readReply } from './protocol.ts';
import type { DocxWorkerJob } from './protocol.ts';

/** The book as a DOCX export takes it: the book, each chapter's IDs, and whether every chapter's base was pinned (PN6). */
export interface DocxBook {
  readonly book: PdfBook;
  readonly chapters: readonly ChapterIds[];
  readonly kept: boolean;
}

/** What the export shows (K″-rows' outcomes); a failure keeps the preset Try again asks for. */
export type DocxState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'reading' }
  | { readonly kind: 'exporting'; readonly done: number; readonly total: number }
  | { readonly kind: 'ready'; readonly url: string; readonly name: string; readonly size: number; readonly missing: number; readonly kept: boolean }
  | { readonly kind: 'failed'; readonly reason: 'unreadable' | 'failed'; readonly preset: DocxPreset };

/** A DOCX worker, as far as the controller drives it (a `Worker`). */
export interface DocxWorker {
  addEventListener(type: 'message' | 'messageerror' | 'error', listener: (event: unknown) => void): void;
  postMessage(message: DocxWorkerJob): void;
  terminate(): void;
}

/** What the controller needs; everything with an effect is injected. */
export interface DocxOptions {
  /** The open book as the export takes it, its bases pinned as of `modified` (table PN), or null when it can't be read whole. */
  readonly readBook: (modified: number) => Promise<DocxBook | null>;
  /** Starts a DOCX worker. */
  readonly worker: () => DocxWorker;
  /** The time, in milliseconds since the epoch: the document's modified stamp and the pins' start (P″3, PN4). */
  readonly now: () => number;
  /** An object URL for the file (`URL.createObjectURL`), and what lets it go. */
  readonly objectUrl: (file: Blob) => string;
  readonly revokeUrl: (url: string) => void;
}

/** A message event's data; undefined for anything else. */
function dataOf(event: unknown): unknown {
  return typeof event === 'object' && event !== null ? Reflect.get(event, 'data') : undefined;
}

/** K″1: the file's name: the book's title, and a manuscript's marked as one. */
export function docxName(title: string, preset: DocxPreset): string {
  return fileName(preset.kind === 'theme' ? title : `${title.trim()} manuscript`, 'docx');
}

/** The DOCX export's state and its one worker. */
export class DocxController {
  readonly state: Accessor<DocxState>;
  readonly #setState: (state: DocxState) => void;
  readonly #options: DocxOptions;
  /** The export under way: a new number for each, so what an earlier one sends is ignored. */
  #job = 0;
  #worker: DocxWorker | null = null;

  constructor(options: DocxOptions) {
    this.#options = options;
    const [state, setState] = createSignal<DocxState>({ kind: 'idle' });
    this.state = state;
    this.#setState = setState;
  }

  /** K″1, K″2: exports the book in `preset` as it is now; nothing while one is under way. A file offered before is let go. */
  start(preset: DocxPreset): void {
    const kind = this.state().kind;
    if (kind === 'reading' || kind === 'exporting') return;
    this.#forget();
    this.#job += 1;
    const job = this.#job;
    this.#setState({ kind: 'reading' });
    const modified = this.#options.now();
    this.#options.readBook(modified).then(
      (read) => {
        if (job !== this.#job) return;
        // K″7: a chapter that can't be read is never left out of the book.
        if (read === null) this.#setState({ kind: 'failed', reason: 'unreadable', preset });
        else this.#run(job, read, preset, modified);
      },
      () => {
        // K″11.
        this.#end(job, { kind: 'failed', reason: 'failed', preset });
      },
    );
  }

  /** K″8: Try again, in the preset that failed; nothing unless one has. */
  retry(): void {
    const state = this.state();
    if (state.kind === 'failed') this.start(state.preset);
  }

  /** K″4–K″6: stops the export under way, keeping nothing of it; a file offered stays offered. */
  cancel(): void {
    const kind = this.state().kind;
    if (kind !== 'reading' && kind !== 'exporting') return;
    this.#job += 1;
    this.#end(this.#job, { kind: 'idle' });
  }

  /** K″9: puts the offer or the failure away. */
  dismiss(): void {
    const kind = this.state().kind;
    if (kind !== 'ready' && kind !== 'failed') return;
    this.#forget();
    this.#setState({ kind: 'idle' });
  }

  /** K″10: the app closing: nothing under way, nothing kept. */
  stop(): void {
    this.cancel();
    this.dismiss();
  }

  /** K″1, K″3, K″8, K″12, K″13: starts the worker on the book and follows what it sends while it is the export under way. */
  #run(job: number, read: DocxBook, preset: DocxPreset, modified: number): void {
    const failed: DocxState = { kind: 'failed', reason: 'failed', preset };
    let worker: DocxWorker;
    try {
      worker = this.#options.worker();
    } catch {
      this.#end(job, failed);
      return;
    }
    this.#worker = worker;
    const fail = (): void => {
      this.#end(job, failed);
    };
    worker.addEventListener('error', fail);
    worker.addEventListener('messageerror', fail);
    worker.addEventListener('message', (event) => {
      if (job !== this.#job) return;
      const reply = readReply(dataOf(event));
      if (reply === null || reply.kind === 'failed') fail();
      else if (reply.kind === 'progress') this.#setState({ kind: 'exporting', done: reply.done, total: reply.total });
      else {
        const { file, missing } = reply;
        this.#end(job, { kind: 'ready', url: this.#options.objectUrl(file), name: docxName(read.book.metadata.title, preset), size: file.size, missing, kept: read.kept });
      }
    });
    this.#setState({ kind: 'exporting', done: 0, total: read.book.chapters.reduce((sum, chapter) => sum + chapter.text.length, 0) });
    worker.postMessage({ kind: 'docx', book: read.book, chapters: read.chapters, preset, modified });
  }

  /** K″4, K″5, K″8: ends the export `job` with `state`, the worker with it; nothing for a job no longer under way. */
  #end(job: number, state: DocxState): void {
    if (job !== this.#job) return;
    this.#worker?.terminate();
    this.#worker = null;
    this.#setState(state);
  }

  /** K″1, K″9, K″10: lets go of a file offered. */
  #forget(): void {
    const state = this.state();
    if (state.kind === 'ready') this.#options.revokeUrl(state.url);
  }
}
